import React from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import trendDown from '../../assets/trendDown.svg';
import trendUp from '../../assets/trendUp.svg';
import targetArrow from '../../assets/targetArrow.svg';
import { metricValueFormat } from '../../utils/metricValueFormat';
import { MetricGaugeVisual } from '../metricGaugeVisual/MetricGaugeVisual';
import {
    MetricTargetTier,
    getMeetsTarget,
    getExceedsTarget,
    getTieredStatusVisual,
    isTieredScorecard,
} from '../../utils/metricAttainment';
import { TieredMetricColors, safeMetricColorIndex } from '../metricMonthlySummaryCard/GradientDefs';
import {
    TieredTargetIcon,
    TieredCircleCheckIcon,
    TieredCircleXIcon,
} from '../metricMonthlySummaryCard/tieredMetricIcons';

interface MetricDetailsProps {
    metric: {
        label: string;
        target: number;
        metric_value: number;
        metric_type_name: string;
        metric_direction_id: number;
        metric_attained: boolean;
        metric_omitted: boolean;
        // Drives legacy vs. tiered (P4P Meets/Exceeds) rendering - see ScorecardCalculationRule.
        scorecard_calculation_rule_id?: number;
        min?: number;
        max?: number;
        targets?: MetricTargetTier[] | null;
        attainment_rank?: number | null;
        metric_attainment_level_label?: string | null;
    };
    index: number;
}

export const MetricCircularChart = (props: MetricDetailsProps) => {
    const {
        label,
        target,
        metric_value: metricValue,
        metric_type_name: metricTypeName,
        metric_direction_id: metricDirectionId,
        metric_attained: metricAttained,
        metric_omitted: metricOmitted,
        scorecard_calculation_rule_id: scorecardCalculationRuleId,
        min,
        max,
        targets,
        attainment_rank: attainmentRank,
        metric_attainment_level_label: attainmentLevelLabel,
    } = props?.metric || {};

    // Rule id 2 (ScorecardCalculationRule.TieredTarget) gets the new Meets/Exceeds gauge;
    // everything else keeps the existing single-target ring.
    const isTiered = isTieredScorecard(scorecardCalculationRuleId);
    const meetsTarget = getMeetsTarget(targets);
    const exceedsTarget = getExceedsTarget(targets);
    const colorIndex = safeMetricColorIndex(props?.index ?? 0, metricOmitted);
    const tieredColorPair = TieredMetricColors[colorIndex] ?? TieredMetricColors[0];
    const tieredStatusVisual = getTieredStatusVisual(attainmentLevelLabel, attainmentRank);
    const tieredStatusColor = metricOmitted ? '#3F5765' : tieredStatusVisual.color;

    const getGradient = (attained?: boolean) => {
        const met = [
            ['#FFD34E', '#FF870A'],
            ['#0CD9BF', '#067365'],
            ['#4AC7FF', '#0D56AC'],
            ['#A9A5FF', '#685FBA'],
            ['#EB419E', '#7D1F78'],
        ];
        const notMet = [
            ['#FFD34E', '#FF870A'],
            ['#16ECD1', '#0B9F8C'],
            ['#4AC7FF', '#166EFF'],
            ['#A9A5FF', '#685FBA'],
            ['#EB419E', '#7D1F78'],
        ];
        const list = attained ? met : notMet;
        const idx = (props?.index ?? 0) % list.length;
        return list[idx];
    };

    const [fromColor, toColor] = getGradient(metricAttained);
    const gradientId = `gradient-${props?.index}`;
    const formattedTarget = metricValueFormat(target, metricTypeName);
    const formattedValue = metricValueFormat(metricValue, metricTypeName);
    const meetsTargetText = meetsTarget !== null ? metricValueFormat(meetsTarget, metricTypeName) : '--';
    const exceedsTargetText = exceedsTarget !== null ? metricValueFormat(exceedsTarget, metricTypeName) : '--';

    const getDirectionIcon = (id?: number) => {
        if (id === 1) return trendUp;
        if (id === 2) return trendDown;
        return null;
    };

    const getDirectionText = (id?: number) => {
        if (id === 1) return 'Ascending';
        if (id === 2) return 'Descending';
        return '';
    };

    const getTextColor = () => {
        if (metricOmitted) return '#3F5765';
        if (metricAttained) return '#2AA154';
        return '#C90370';
    };

    // Precomputed to avoid a nested ternary in JSX (matches MetricMonthlySummaryCard's approach).
    let statusContent: React.ReactNode;
    if (metricOmitted) {
        statusContent = <span className="MetricOmitted">Omitted Metric</span>;
    } else if (isTiered) {
        statusContent = (
            <span className="metricTieredStatus" style={{ color: tieredStatusColor }}>
                {tieredStatusVisual.icon === 'cross' ? <TieredCircleXIcon /> : <TieredCircleCheckIcon />}
                <span className="metricTieredStatus__label">{attainmentLevelLabel ?? '--'}</span>
            </span>
        );
    } else {
        statusContent = (
            <div className={`metric-status ${!metricAttained ? 'not-met' : ''}`}>
                <i className={`pi ${metricAttained ? 'pi-check-circle' : 'pi-times-circle'}`} />
                {metricAttained ? 'Metric Met' : 'Metric Not Met'}
            </div>
        );
    }

    return (
        <div className="modern-metric-card" role="button" tabIndex={0}>
            <div className="metric-header">
                <h5 title={label}>{label}</h5>
                {metricDirectionId !== undefined && (
                    <div className="direction-inline">
                        <img
                            src={getDirectionIcon(metricDirectionId) || ''}
                            alt="trend"
                            className="trend-icon"
                        />
                        <span className="direction-text">{getDirectionText(metricDirectionId)}</span>
                    </div>
                )}
            </div>

            {isTiered ? (
                <div
                    className="progress-wrapper"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: '4px',
                    }}
                >
                    <div style={{ width: '150px', height: '100px', flexShrink: 0, overflow: 'hidden' }}>
                        <div style={{ width: '200px', height: '200px', transform: 'scale(0.75)', transformOrigin: 'top left' }}>
                            <MetricGaugeVisual
                                variant="tiered"
                                valueText={formattedValue}
                                colorIndex={colorIndex}
                                metricOmitted={metricOmitted}
                                attainmentRank={attainmentRank}
                                attainmentLevelLabel={attainmentLevelLabel}
                                metricValue={metricValue}
                                min={min}
                                max={max}
                                meetsTarget={meetsTarget}
                                exceedsTarget={exceedsTarget}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="progress-wrapper">
                    <svg style={{ height: 0 }}>
                        <defs>
                            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor={fromColor} />
                                <stop offset="100%" stopColor={toColor} />
                            </linearGradient>
                        </defs>
                    </svg>
                    <CircularProgressbar
                        maxValue={target}
                        value={metricValue}
                        text={formattedValue}
                        styles={buildStyles({
                            pathColor: `url(#${gradientId})`,
                            trailColor: '#E6EAF3',
                            textColor: getTextColor(),
                            textSize: '20px',
                            strokeLinecap: 'round',
                        })}
                        strokeWidth={15}
                    />
                </div>
            )}

            <div className="metric-footer">
                <div className="col-12 text-center">{statusContent}</div>
                {isTiered ? (
                    <div className="metricTargetThresholds text-center">
                        <div className="metricTargetThresholds__row" style={{ color: tieredColorPair.dark }}>
                            <TieredTargetIcon />
                            <span>Meets at: {meetsTargetText}</span>
                        </div>
                        <div className="metricTargetThresholds__row" style={{ color: tieredColorPair.light }}>
                            <TieredTargetIcon />
                            <span>Exceeds at: {exceedsTargetText}</span>
                        </div>
                    </div>
                ) : (
                    <div className="target-label">
                        <img src={targetArrow} alt="Target" />
                        Target: {formattedTarget}
                    </div>
                )}
            </div>
        </div>
    );
};
